import { HTMLAttributes, ReactNode, forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { getScrollContainer } from 'src/Utils';
import st from "./Popover.module.scss";

interface IPopoverProps extends HTMLAttributes<HTMLDivElement> {

	isOpened: boolean;
	setIsOpen: ( isShow: boolean ) => void;

	isNeedRecalcPos: boolean;
	align?: "left" | "right";
	
	onOutsideClick: ( event: MouseEvent ) => void;

	renderBody: () => ReactNode;
	bodyClass?: string;
	isRevertClass?: string;
};

const Popover = forwardRef<HTMLDivElement,IPopoverProps>( ( props, ref ) => {

	const { isOpened, isNeedRecalcPos, bodyClass, isRevertClass = st.upper, align = "left",
		setIsOpen, onOutsideClick, renderBody,
		children, className, ...otherProps 
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const [ position, setPosition ] = useState<PositionType>(null);

	/** Подписываемся на событие скролла */
	useLayoutEffect( () => {
		const wr = wrapperRef.current;
		const scrollContainer = getScrollContainer( wr );
		const onClose = () => setIsOpen(false);
		if ( !!scrollContainer ) {
			scrollContainer.addEventListener( "scroll", onClose );
		}
		return () => {
			if ( !!scrollContainer ) {
				scrollContainer.removeEventListener( "scroll", onClose );
			}
		};
	});

	/** При открытии и изменениях в опциях, обновляем позицию выпадающего списка */
	useEffect( () => {
		calcOffsetModalPosition();
	// eslint-disable-next-line
	}, [ isOpened, isNeedRecalcPos ] );

	useEffect( () => {
		if ( !isOpened ) return;
		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if ( modalRef.current?.contains( target ) ) return;
			if ( wrapperRef.current?.contains( target ) ) return;
			onOutsideClick( event );
		};
		document.addEventListener( "mouseup", handleOutsideClick );
		return () => {
			document.removeEventListener( "mouseup", handleOutsideClick );
		};
	}, [ isOpened, onOutsideClick ] );

	/** Вычисляет позицию модалки выпадающего списка */
	const calcOffsetModalPosition = () => {
		const wr = wrapperRef.current;
		if ( !wr ) return;

		const rect = wr.getBoundingClientRect();
		const height = modalRef.current?.clientHeight || 430;
		const modalWidth = modalRef.current?.clientWidth || rect.width;
		let top = rect.bottom;
		let left = align === "right" ? rect.right - modalWidth : rect.left;

		if ( ( top + ( height + 50 ) ) > window.innerHeight ) {
			wrapperRef.current.classList.add( isRevertClass );
			modalRef.current?.classList.add( isRevertClass );
			top = rect.top - height;
		}
		else if ( modalRef.current?.classList.contains( isRevertClass ) ) {
			modalRef.current?.classList.remove( isRevertClass );
			wrapperRef.current.classList.remove( isRevertClass );
		}

		if ( (left + modalWidth) > window.innerWidth ) {
			left = Math.max(10, window.innerWidth - modalWidth - 10);
		}
		if ( left < 10 ) left = 10;

		setPosition( { top, left, width: `${rect.width}px` } );
	};

	/** Отрисовка модалки выпадающего списка */
	const renderBodyWrapper = () => (
		<div
			ref={modalRef} 
			className={classNames("body-small", st.bodyList, bodyClass)}
			style={{...position }}
		>
			{ renderBody() }
		</div>
	);

	return (
		<div ref={wrapperRef} className={classNames( st.wrapper, className)} {...otherProps}>
			{ children }
			{ isOpened && createPortal( renderBodyWrapper(), document.body) }
		</div>
	);
});

/** Тип позиции ( top, left ) */
type PositionType = {
	top: number;
	left: number;
	width: string;
} | null;

export default Popover;
